import { JSX, useContext, useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  XAxis,
  YAxis,
  LabelProps,
  Legend,
  ResponsiveContainer,
  Bar,
  Tooltip
} from 'recharts';
import { NetworkIntegrityWidgetInfoFlyout, ProviderIntegrityTrendDescription, ProviderIntegrityTrendTooltip, WidgetMenu } from '@app/projects/network-assessment/network-integrity/widgets';
import { RoundedLegendIcon } from '@app/projects/network-assessment/network-integrity/widgets/RoundedLegendIcon';
import { QesColors } from "@app/shared/qes-standards/colors";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityService from '@app/projects/network-assessment/network-integrity/networkIntegrityService';
import { NetworkIntegrityProviderTrend } from '@app/projects/network-assessment/network-integrity/models';
import { NetworkIntegrityJobStatus } from "@app/projects/enumerators";
import { Widget, WidgetBody, WidgetHeader, WidgetLoading } from '@app/projects/network-assessment/network-integrity/widgets/Widget';
import { NetworkIntegrityDisplayMode } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { ResourceType } from '@app/resources';

const ProviderIntegrityTrendWidget = (): JSX.Element => {
  const title = 'Integrity Trend';

  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const project = networkIntegrityContext.project!;
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const showAfterTermed = networkIntegrityContext.displayMode === NetworkIntegrityDisplayMode.TotalAfterTermed;

  const [loading, setLoading] = useState<boolean>(false);
  const [providerTrendResults, setProviderTrendResults] = useState<NetworkIntegrityProviderTrend[]>(null);

  useEffect(() => {
    let cancelled = false;
    setProviderTrendResults(null);
    async function fetchProviderTrendResults() {
      try {
        if (networkIntegrityContext.projectRunConfigurationId) {
          const results = await networkIntegrityService.getProviderBreakdownTrendResults(
            project.projectId,
            networkIntegrityContext.projectRunConfigurationId,
            networkIntegrityContext.providerType,
            networkIntegrityContext.projectRunConfigurationDualType,
            networkIntegrityContext.regionSetIds,
            networkIntegrityContext.specialtyCodes
          );

          if (!cancelled) setProviderTrendResults(results);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (networkIntegrityContext.isJobStatusLoading === false && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished) {
      setLoading(true);
      fetchProviderTrendResults();
    }

    return () => { cancelled = true; };
  }, [networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.jobStatus, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes]);

  if (networkIntegrityContext.isLoading || loading || !networkIntegrityContext.jobStatus)
    return (<WidgetLoading additionalClassNames="ni-trend-container"><WidgetHeader title={title} /></WidgetLoading>);

  const red = QesColors.QesTertiaryRed2;
  const yellow = QesColors.QesTertiaryYellow2;
  const blue = QesColors.QesTertiaryBlue1;
  const gray = QesColors.QesPrimaryGray4;

  const legendData = [
    { color: red, value: 'Providers With Alerts' },
    { color: yellow, value: 'Providers With Warnings Only' },
    { color: blue, value: 'Providers With No Alerts or Warnings' }
  ]

  const renderLegendText = (value: string | number) => {
    return (
      <span className='color-logan demibold ni-trend-container__legend-item-text'>{value}</span>
    );
  };

  const renderTrendLegend = () => (
    <div>
      {legendData.map((item) => (
        <div key={item.value} className="ni-trend-container__legend-item">
          <svg width={12} height={12} viewBox="0 0 32 32" style={{ display: "inline-block", marginRight: 6 }}>
            <RoundedLegendIcon fill={item.color} />
          </svg>
          {renderLegendText(item.value)}
        </div>
      ))}
    </div>
  );

  const getLast5Months = () => {
    const currentDate = new Date();
    const last5Months = [];

    for (let i = 0; i < 5; i++) {
      const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const month = firstDayOfMonth.toLocaleString('default', { month: 'short' });
      last5Months.push(month);
    }

    return last5Months.reverse();
  }

  const emptyTrendData = getLast5Months().map(month => ({
    value: month,
  }));

  const setTrendChartData = (providerTrendResults: NetworkIntegrityProviderTrend[]) => {
    let lastNonZeroAggregate = 0;

    return providerTrendResults.map((result) => {
      const isZeroCounts = (result.providerCountWithAlerts ?? 0) === 0
        && (result.providerCountWithOnlyWarnings ?? 0) === 0
        && (result.providerCountWithNoAlertsOrWarnings ?? 0) === 0;

      const currentAggregate =
        (result.providerCountWithAlerts ?? 0) +
        (result.providerCountWithOnlyWarnings ?? 0) +
        (result.providerCountWithNoAlertsOrWarnings ?? 0);

      let gray = 0, blue = 0, yellow = 0, red = 0;

      if (isZeroCounts) {
        gray = lastNonZeroAggregate;
      } else {
        blue = result.providerCountWithNoAlertsOrWarnings;
        yellow = result.providerCountWithOnlyWarnings;
        red = result.providerCountWithAlerts;
        lastNonZeroAggregate = currentAggregate;
      }

      return {
        blue,
        gray,
        name: new Date(result.projectRunDate).toLocaleString('default', { month: 'short' }),
        projectRunDate: result.projectRunDate,
        red,
        transparent: 0, // needed only for possible Unpublished run
        yellow
      };
    });
  }

  // Skip trailing gray-fill entries to find the newest month with real data.
  const latestRealResult = providerTrendResults && providerTrendResults.length > 0
    ? [...providerTrendResults].reverse().find(r => !r.isFillMonth) ?? null
    : null;

  const setTrendingNotLicensedChartData = () => {
    const padded = emptyTrendData.map(() => ({ blue: 0, gray: 0, name: "", red: 0, transparent: 0, yellow: 0 }));
    if (latestRealResult) padded[padded.length - 1] = setTrendChartData([latestRealResult])[0];
    return padded;
  }

  const isZipProject = project.resource.resourceType === ResourceType.ZipAnalysisProject;
  let trendChartData =
    providerTrendResults && providerTrendResults.length > 0
      ? !project.showAdvancedComparison && !isZipProject
        ? setTrendingNotLicensedChartData()
        : setTrendChartData(providerTrendResults)
      : emptyTrendData.map(m => ({ blue: 0, gray: 0, name: m.value, red: 0, transparent: 0, yellow: 0 }));

  const showNotYetPublished = latestRealResult
    && latestRealResult.isPublished === false
    && (latestRealResult.providerCountWithAlerts + latestRealResult.providerCountWithOnlyWarnings + latestRealResult.providerCountWithNoAlertsOrWarnings) > 0;

  const maxCount = Math.max(...trendChartData.map(d => (d.red || 0) + (d.yellow || 0) + (d.blue || 0) + (d.gray || 0)), 0);

  if (showNotYetPublished && trendChartData.length > 0) {
    trendChartData = trendChartData.map((item, idx) => {
      if (idx === trendChartData.length - 1) {
        const sum = (item.blue || 0) + (item.yellow || 0) + (item.red || 0);
        return { ...item, transparent: maxCount - sum };
      }
      return item;
    });
  }

  const afterTermedTotal = showAfterTermed && latestRealResult
    ? (latestRealResult.afterTermedProviderCountWithAlerts ?? 0)
      + (latestRealResult.afterTermedProviderCountWithOnlyWarnings ?? 0)
      + (latestRealResult.afterTermedProviderCountWithNoAlertsOrWarnings ?? 0)
    : 0;

  if (showAfterTermed && latestRealResult) {
    trendChartData = [...trendChartData, {
      blue: latestRealResult.afterTermedProviderCountWithNoAlertsOrWarnings ?? 0,
      gray: 0,
      name: 'Total After Termed',
      red: latestRealResult.afterTermedProviderCountWithAlerts ?? 0,
      transparent: showNotYetPublished ? Math.max(maxCount - afterTermedTotal, 0) : 0,
      yellow: latestRealResult.afterTermedProviderCountWithOnlyWarnings ?? 0
    }];
  }

  const totalMax = Math.max(maxCount, afterTermedTotal);

  const roundedMax = (totalMax >= 10000)
                    ? Math.ceil(totalMax / 10000) * 10000
                    : (totalMax < 10000 && totalMax >= 1000)
                      ? Math.ceil(totalMax / 1000) * 1000
                      : Math.ceil(totalMax / 100) * 100 || 100;

  const lastMonthlyBarIndex = showAfterTermed && afterTermedTotal > 0
    ? trendChartData.length - 2
    : trendChartData.length - 1;

  const renderNotYetPublishedLabel = (
    props: LabelProps
  ): JSX.Element | null => {
    if (
      showNotYetPublished &&
      (props.index === lastMonthlyBarIndex || (showAfterTermed && latestRealResult && props.index === trendChartData.length - 1)) &&
      typeof props.x === 'number' &&
      typeof props.width === 'number' &&
      typeof props.y === 'number' &&
      typeof props.height === 'number'
    ) {
      const centerX = props.x + props.width / 2;
      const transparentBarTop = props.y;

      return (
        <g>
          <defs>
            <filter id="gray-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="7" result="blur" />
              <feOffset in="blur" dx="0" dy="0" result="offsetBlur" />
              <feFlood floodColor="#222" floodOpacity="1" result="color" />
              <feComposite in2="offsetBlur" in="color" operator="in" result="glow" />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <text
            x={centerX}
            y={transparentBarTop + 8}
            textAnchor="end"
            transform={`rotate(-90,${centerX},${transparentBarTop})`}
            style={{
              fontSize: '1.3rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              pointerEvents: 'none',
              userSelect: 'none'
            }}
            fill="#fff"
            filter="url(#gray-glow)"
          >
            Not Yet Published
          </text>
        </g>
      );
    }
    return null;
  };

  const renderTrendingUnlicensedLabel = (): JSX.Element => {
    return (
      <g className="ni-trend-unlicensed" pointerEvents="none" style={{ userSelect: 'none' }}>
        {/* left solid line */}
        <line
          x1="22%"
          x2="32%"
          y1="54%"
          y2="54%"
          stroke={QesColors.QesPrimaryGray4}
          strokeWidth={1}
          strokeLinecap="round"
        />
        {/* label */}
        <text
          x="35%"
          y="55%"
          textAnchor="start"
          className="color-logan demibold"
          style={{ fontSize: '0.7rem' }}
          fill={QesColors.QesPrimaryGray4}
        >
          Trending is not licensed
        </text>
        {/* right solid line */}
        <line
          x1="71%"
          x2="81%"
          y1="54%"
          y2="54%"
          stroke={QesColors.QesPrimaryGray4}
          strokeWidth={1}
          strokeLinecap="round"
        />
      </g>
    );
  };

  return (
    <Widget additionalClassNames='ni-trend-container'>
      <WidgetHeader title={title}>
        <WidgetMenu>
          <NetworkIntegrityWidgetInfoFlyout title="Network Provider Integrity Trend" description={<ProviderIntegrityTrendDescription/>}/>
        </WidgetMenu>
      </WidgetHeader>
      <WidgetBody>
          <ResponsiveContainer width="90%" height="75%" style={{ bottom: 10, left: 0, position: 'absolute' }}>
            <BarChart
              data={trendChartData}
              margin={{
                bottom: 0,
                left: 0,
              }}
            >
            {/* Tick text styling has to go on `tick`, not on the axis: recharts 3 renders tick
                labels through a z-index portal, so they are no longer DOM children of the axis
                group and nothing cascades down from the axis className. */}
            <XAxis dataKey="name" tickLine={false}
              interval={0}
              tick={{
                className: "color-logan ni-trend-container__empty-trend-axis",
                textAnchor: 'middle',
                width: 36
              }}
              height={36}
            />
              <YAxis domain={[0, roundedMax]} tickLine={false} tick={{ className: "color-logan ni-trend-container__empty-trend-axis" }} />

              <Bar dataKey="blue" stackId="a" fill={blue} barSize={36} activeBar={false} />
              <Bar dataKey="yellow" stackId="a" fill={yellow} barSize={36} activeBar={false} />
              <Bar dataKey="red" stackId="a" fill={red} barSize={36} activeBar={false} />
              <Bar dataKey="gray" stackId="a" fill={gray} barSize={36} activeBar={false} />
              <Bar
                dataKey="transparent"
                stackId="a"
                fill="transparent"
                barSize={36}
                activeBar={false}
                label={renderNotYetPublishedLabel}
              />

              {providerTrendResults && providerTrendResults.length === 0 && (
                <text
                  x="50%"
                  y="55%"
                  textAnchor="middle"
                  className='color-logan demibold'
                  style={{ fontSize: '1.2rem' }}
                >
                  -- No Data --
                </text>
              )}

              {!project.showAdvancedComparison && !isZipProject && (
                renderTrendingUnlicensedLabel()
              )}

              <Legend
                // Not `position="top"`: that bottom-anchors the wrapper with
                // translate(-50%, -100%), so any extra legend height grows upward into the widget
                // title. verticalAlign sets the wrapper's top edge, so it grows downward instead.
                verticalAlign="top"
                wrapperStyle={{
                  left: "max(10%, 30px)",
                  top: "-12%",
                  whiteSpace: "break-spaces",
                }}
                content={renderTrendLegend()}
              />
              <Tooltip content={ProviderIntegrityTrendTooltip} cursor={false} />
            </BarChart>
          </ResponsiveContainer>
      </WidgetBody>
    </Widget>);
};

export { ProviderIntegrityTrendWidget };